import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { GameAudioProvider, useResolvedGameAudioOn } from '@/shared/hooks/useGameAudioSettings';
import config from '@/config';
import { EndOutcome } from '@/features/gameSession';
import { useTracingGamePhaserGame } from '../hooks/useTracingGamePhaserGame';
import { useTracingGameVoiceAudio } from '../hooks/useTracingGameVoiceAudio';
import { EventBus, EVENTS } from '../game/events/tracingGameEventBus';

import AudioToggleButton from '@/shared/components/audioToggle/AudioToggleButton';
import BackButton from '@/shared/components/button/BackButton';
import Button from '@/shared/components/button/Button';

import '../styles/tracing-game.scss';

import type { CSSProperties, MouseEvent } from 'react';
import type { GameAudioProps, GameCloseEvent, GameFinishEvent, GameFinishHandler } from '@/features/gameSession';
import type { TracingGameData } from '../types';

export type TracingGameProps = {
	className?: string;
	style?: CSSProperties;
	gameData?: TracingGameData | null;
	onClose: (event?: GameCloseEvent) => void;
	onStart?: () => void;
	onFinish?: GameFinishHandler;
} & GameAudioProps;

type TracingGameContentProps = Omit<TracingGameProps, 'audioOn' | 'onAudioChange'>;

const TracingGameContent = ({ className, gameData, onClose, onStart, onFinish, style }: TracingGameContentProps) => {
	const { t } = useTranslation();
	const audioOn = useResolvedGameAudioOn();
	const containerRef = useRef<HTMLDivElement | null>(null);
	const isPlayingRef = useRef(false);
	const closePendingRef = useRef(false);
	const { play: playVoiceLine, stop: stopVoiceLine } = useTracingGameVoiceAudio(
		gameData?.presentation.voice_audio,
		audioOn,
	);
	const handleStart = useCallback(
		(onIntroComplete: () => void) => {
			isPlayingRef.current = true;
			playVoiceLine('intro', onIntroComplete);
			onStart?.();
		},
		[onStart, playVoiceLine],
	);
	const handleFailure = useCallback((onComplete: () => void) => playVoiceLine('fail', onComplete), [playVoiceLine]);
	const handleVictory = useCallback(() => playVoiceLine('complete'), [playVoiceLine]);
	const handleInactive = useCallback(() => {
		if (!closePendingRef.current) playVoiceLine('inactive');
	}, [playVoiceLine]);
	const handleSkip = useCallback(() => {
		if (!closePendingRef.current) playVoiceLine('skip');
	}, [playVoiceLine]);
	const handleFinish = useCallback(
		(event: GameFinishEvent, onFinished?: () => void) => {
			isPlayingRef.current = false;
			if (event.gameOutcome === EndOutcome.SUCCEEDED) {
				closePendingRef.current = true;
				onFinished = onClose;
			}
			if (onFinish) {
				onFinish(event, onFinished);
			} else {
				onFinished?.();
			}
		},
		[onClose, onFinish],
	);
	const handleClose = () => {
		if (closePendingRef.current) return;

		stopVoiceLine();
		if (isPlayingRef.current) {
			closePendingRef.current = true;
			handleFinish({ gameOutcome: EndOutcome.ABANDONED, correctRatio: 0 }, onClose);
			return;
		}

		onClose();
	};
	const combinedClassName = ['tracing-game', className].filter(Boolean).join(' ');

	useTracingGamePhaserGame({
		audioOn,
		containerRef,
		debug: config.debug,
		gameData,
		onStart: handleStart,
		onFailure: handleFailure,
		onVictory: handleVictory,
		onInactive: handleInactive,
		onSkip: handleSkip,
		onFinish: handleFinish,
	});

	const handleBackClick = (event: MouseEvent<HTMLButtonElement>) => {
		if (event.detail === 0) {
			handleClose();
		}
	};
	const handleBackPointerDown = () => handleClose();
	const handleRetry = () => {
		stopVoiceLine();
		EventBus.emit(EVENTS.RETRY_GAME);
	};

	return (
		<div className={combinedClassName} style={style}>
			<div className="tracing-game__actions">
				<BackButton onClick={handleBackClick} onPointerDown={handleBackPointerDown} small />
				<AudioToggleButton tooltip={t('tracing_game.audio_toggle.tooltip')} hiddenTooltip small />
			</div>
			<div className="tracing-game__retry">
				<Button
					btnType="button"
					iconName="icon-replay"
					color="white"
					type="secondary"
					rounded
					small
					onClick={handleRetry}
					className="tracing-game__retry-button"
				/>
			</div>
			<div className="tracing-game__stage" ref={containerRef} />
		</div>
	);
};

const TracingGame = ({ audioOn, onAudioChange, ...props }: TracingGameProps) => (
	<GameAudioProvider audioOn={audioOn} onAudioChange={onAudioChange}>
		<TracingGameContent {...props} />
	</GameAudioProvider>
);

export default TracingGame;
